<template>
  <AsidePanel
    ref="asidePanel"
    :cancelText="cancelText"
    :components="components"
    :handleMountedCallback="handleMountedCallback"
    :handleSave="handleSave"
    :initialTabIndex="tabIndex"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :navigateToTab="navigateToTab"
    :redirectTo="directoryTypeRoute(itemType)"
    :saveText="saveText"
    :showSaveButton="showSaveButton"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="google"
      />
    </template>
    <template
      v-slot:headerText
    >
      <EditableDirectoryName
        :itemType="itemType"
      />
    </template>
  </AsidePanel>
</template>

<script>
/**
 * @todo: Investigate creating a component for the GSuiteAside and
 * O365Aside - they share a lot of code
 */
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import Constants, {
  DirectoryActionConsts,
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeRoutes,
  LocalStorageConsts,
  StoreModelMap,
  StoreModuleMap,
  UnsavedChangesModalConsts,
} from 'client/util/Constants';
import DirectoryDialogsHelper from 'client/directories/DirectoryDialogsHelper';
import EditableDirectoryName from 'client/directories/components/EditableDirectoryName.vue';
import ExportAttributeWarningModal from 'client/directories/components/ExportAttributeWarningModal';
import GSuiteApi from 'client/api/v2/GSuiteApi';
import GSuiteDetails from 'client/directories/GSuite/GSuiteDetails.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LocalStorageService from 'client/util/LocalStorageService';
import ModalEventBus from 'components/Modal/ModalEventBus';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SystemUserItemTable from 'client/systemusers/SystemUserItemTable/SystemUserItemTable.vue';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';
import Util from 'client/util/Util';
import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';
import pluralize from 'pluralize';

const { create, update } = ItemActionConsts;
const {
  cloudDirectory, gSuite, systemUser, userGroup,
} = ItemTypeConsts;
const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];
const displayName = ItemTypeDisplayNames[gSuite];
const gSuiteModel = StoreModelMap[gSuite];
const userGroupCollection = StoreModuleMap[ItemTypeConsts.userGroup];

export default {
  name: 'GSuiteAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
    EditableDirectoryName,
  },

  mixins: [
    AsidePanelPropsMixin,
    directoryRouteMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: gSuite,
    },
  },

  beforeRouteEnter(to, from, next) {
    const activated = LocalStorageService.getItem('cloudDirectoryActivated');
    const previousRoutes = from.matched.length;
    const reactivated = LocalStorageService.getItem('cloudDirectoryReactivated');
    const typeName = DirectoryNameConsts[DirectoryTypeConsts.gSuite];

    if (previousRoutes >= 0 && activated) {
      LocalStorageService.setItem('cloudDirectoryActivated', DirectoryActionConsts.activated);
    }

    if (previousRoutes === 0 && reactivated) {
      notify(
        Constants.success,
        `The ${typeName} Directory was successfully ${DirectoryActionConsts.reactivated}.`,
      );
    }

    next();
  },

  data() {
    return {
      cancelText: '',
      cloudDirectory: ItemTypeRoutes[cloudDirectory],
      components: [{
        label: 'Details',
        vueComponent: GSuiteDetails,
        vueComponentProps: {
          handleOauthRedirect: this.handleOauthRedirect,
          itemId: this.itemId,
          viewport: this.viewport,
        },
      }],
      duplicateError: undefined,
      isInOauthRedirect: false,
      itemHasBeenSaved: false,
      navigateToTab: undefined,
      saveText: '',
      showSaveButton: false,
    };
  },

  computed: {
    ...mapGetters(directoryCollection, {
      directories: 'getCollection',
    }),

    ...mapGetters(gSuiteModel, [
      'displayName',
      'getExportAttributesKbUrl',
      'hasItemChanged',
      'haveAssociationsChanged',
      'haveExportAttributesChanged',
      'isItemBeingCreated',
      'isItemBeingUpdated',
      'item',
    ]),

    ...mapState(gSuiteModel, {
      previousItem: state => state.previousItem,
    }),

    ...mapState(userGroupCollection, {
      duplicateValidationErrors: state => state.entities.duplicateValidationErrors,
      validationErrors: state => state.entities.validationErrors,
    }),

    announcementInfo() {
      return {
        enabled: this.item.groupsEnabled,
        header: 'Add Distribution Group Email',
        intro: 'NEW',
        linkValue: 'https://support.google.com/a/answer/167429?hl=en',
        linkText: 'New groups don’t show up in Groups directory.',
        localStorageKey: LocalStorageConsts.userGroupDistributionEmail,
        text: `Add an email value for the groups associated with this ${DirectoryNameConsts[DirectoryTypeConsts.gSuite]} directory so that JumpCloud can manage groups and their membership. Be aware that it can take some time for groups to appear in Google. See Google's Admin Help:`,
        tooltipKbLink: KbArticleUrls.distributionGroupManagement,
      };
    },

    tabIndex() {
      const reactivated = LocalStorageService.getItem('cloudDirectoryReactivated');
      if (reactivated) {
        return 1;
      }

      return 0;
    },
  },

  mounted() {
    const activated = LocalStorageService.getItem('cloudDirectoryActivated');
    if (ModalEventBus.isModalOpen()) {
      ModalEventBus.close();
    }
    if (activated === DirectoryActionConsts.activated) {
      DirectoryDialogsHelper.showDirectoryImportUsersModal(
        DirectoryTypeConsts.gSuite,
        this.itemId,
      );
      LocalStorageService.removeItem('cloudDirectoryActivated');
    }
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  methods: {
    ...mapActions(gSuiteModel, [
      'bindAssociations',
      'saveExportAttributes',
      'saveItem',
      'resetItem',
    ]),

    ...mapActions(userGroupCollection, [
      'resetCollection',
    ]),

    deleteLocationHashValue() {
      window.setTimeout(() => PageNavigationUtil.removeLocationHashValue(`${this.cloudDirectory}/g_suite/${this.itemId}/details`), 500);
    },

    getColumns(init) {
      const userGroupHeaderLabel = `Members of the following user groups are syncing to the ${displayName} Instance`;
      const userHeaderLabel = `The following users are syncing to the ${displayName} Instance`;

      this.cancelText = 'cancel';
      this.saveText = 'save';
      this.showSaveButton = true;

      if (init) {
        this.components.push({
          label: 'Details',
          vueComponent: GSuiteDetails,
          vueComponentProps: {
            handleOauthRedirect: this.handleOauthRedirect,
            itemId: this.itemId,
            viewport: this.viewport,
          },
        },
        {
          itemType: userGroup,
          label: 'User Groups',
          headerLabels: {
            [create]: userGroupHeaderLabel,
            [update]: userGroupHeaderLabel,
            useDisplayName: false,
          },
          vueComponent: UserGroupItemTable,
          vueComponentProps: {
            announcement: this.announcementInfo,
            boundItemsUrl: `/api/v2/gsuites/${this.itemId}/usergroups`,
            directoryName: DirectoryNameConsts[DirectoryTypeConsts.gSuite],
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.directoryAsidePanel,
            showDistributionGroupEmail: this.item.groupsEnabled,
            updateItemErrorHandler: this.updateItemErrorHandler,
          },
        }, {
          itemType: systemUser,
          label: 'Users',
          headerLabels: {
            [create]: userHeaderLabel,
            [update]: userHeaderLabel,
            useDisplayName: false,
          },
          vueComponent: SystemUserItemTable,
          vueComponentProps: {
            boundItemsUrl: `/api/v2/gsuites/${this.itemId}/users`,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.directoryAsidePanel,
          },
        });
      } else {
        this.components.push({
          itemType: userGroup,
          label: 'User Groups',
          headerLabels: {
            [create]: userGroupHeaderLabel,
            [update]: userGroupHeaderLabel,
            useDisplayName: false,
          },
          vueComponent: UserGroupItemTable,
          vueComponentProps: {
            announcement: this.announcementInfo,
            boundItemsUrl: `/api/v2/gsuites/${this.itemId}/usergroups`,
            directoryName: DirectoryNameConsts[DirectoryTypeConsts.gSuite],
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.directoryAsidePanel,
            showDistributionGroupEmail: this.item.groupsEnabled,
            updateItemErrorHandler: this.updateItemErrorHandler,
          },
        }, {
          itemType: systemUser,
          label: 'Users',
          headerLabels: {
            [create]: userHeaderLabel,
            [update]: userHeaderLabel,
            useDisplayName: false,
          },
          vueComponent: SystemUserItemTable,
          vueComponentProps: {
            boundItemsUrl: `/api/v2/gsuites/${this.itemId}/users`,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.directoryAsidePanel,
          },
        });
      }
    },

    handleBeforeDestroy() {
      this.handleBeforeRedirect();
      this.resetCollection();
    },

    handleBeforeRedirect() {
      LocalStorageService.removeItem('cloudDirectoryActivated');
      LocalStorageService.removeItem('cloudDirectoryReactivated');
    },

    handleMountedCallback() {
      if (this.isItemBeingCreated) {
        this.cancelText = 'close';
      } else if (this.isItemBeingUpdated) {
        this.getColumns(false);
      }
    },

    handleOauthRedirect(oauthUrl) {
      const { headerText, bodyText } = UnsavedChangesModalConsts;
      const handleCancel = () => {
        ModalEventBus.close();
        this.isInOauthRedirect = false;
      };
      const componentProps = {
        headerText,
        bodyText,
        handleCancel: () => handleCancel(),
        handleContinue: () => Util.redirect(oauthUrl),
        showClose: true,
      };
      this.handleBeforeRedirect();

      if (this.hasModelChanged()) {
        // this flag prevents an additional chrome created modal message on redirect
        this.isInOauthRedirect = true;
        ModalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
          preventCloseOnOverlayClick: true,
        });
      } else {
        try {
          Util.redirect(oauthUrl);
        } catch {
          this.isInOauthRedirect = false;
        }
      }
    },

    async handleSave() {
      const saveData = () => {
        this.itemHasBeenSaved = true;
        const parentType = gSuite;
        const reactivate = this.item.groupsEnabled && !this.previousItem.groupsEnabled;
        const distributionGroupsDisabled = !this.item.groupsEnabled
        && this.previousItem.groupsEnabled;

        // Don't proceed if there are errors from user groups tab.
        if (this.validationErrors && this.validationErrors.length > 0) throw new Error('The email field must be a valid email.');
        if (this.duplicateValidationErrors && this.duplicateValidationErrors.length > 0) throw new Error('The email field must be  unique.');

        if (distributionGroupsDisabled) this.components = [];

        return this.saveItem()
          .then(() => this.saveExportAttributes())
          .then(() => this.bindAssociations({ parentType, targetType: userGroup }))
          .then(() => this.bindAssociations({ parentType, targetType: systemUser }))
          .then(() => {
            let canContinue = true;
            if (reactivate) {
              // Reactivate G Suite a short time after closing the aside and displaying the success
              // status to the admin.
              window.setTimeout(() => this.reactivateOauth(), 500);
            } else {
              canContinue = false;
              if (distributionGroupsDisabled) this.getColumns(true);
              this.navigateToUserGroupsTab();
            }
            return new SaveReturnValue({ canContinue });
          })
          .catch((err) => {
            throw err instanceof Error ? err : Error('Unknown error');
          });
      };

      // Don't save if the directory name is invalid.
      if (this.v2Errors.any()) {
        throw new Error(this.v2Errors.items[0].msg);
      }

      if (this.haveExportAttributesChanged) {
        const canContinue = await this.showWarningModal();
        if (canContinue === false) {
          return new SaveReturnValue({ canContinue });
        }
      }

      return saveData();
    },

    hasModelChanged() {
      if (this.isInOauthRedirect) {
        return false;
      }
      if (this.itemHasBeenSaved) {
        return false;
      }

      return this.haveExportAttributesChanged
        || this.haveAssociationsChanged([userGroup, systemUser])
        || this.hasItemChanged;
    },

    navigateToUserGroupsTab() {
      const userGroupsTabLabel = pluralize(ItemTypeDisplayNames[ItemTypeConsts.userGroup]);
      this.navigateToTab = { tabLabel: userGroupsTabLabel };
    },

    /**
     * @todo Part of the code below is repeated in the <GSuiteDetails> component which also knows
     * how to get the reactivate URL. Consider modifying the GSuiteApi.reactivate() method to
     * simply return the reactiavateUrl to the caller to DRY out this code. Another alternative is
     * to simply have the reactivateAction() method in the <GSuiteDetails> component call the
     * handleOauthRedirect() method and reuse the reactivateOAuth() method there.`
     */
    async reactivateOauth() {
      const reactivateUrl = await GSuiteApi.reactivate(this.item.id);
      const oauthUrl = get(reactivateUrl, 'data.location');
      Util.redirect(oauthUrl);
    },

    showWarningModal() {
      return new Promise((resolve) => {
        ModalEventBus.open({
          component: ExportAttributeWarningModal,
          componentProps: {
            directoryName: this.displayName,
            exportAttributesKbUrl: this.getExportAttributesKbUrl,
            handleCancel: () => resolve(false),
            handleContinue: () => resolve(true),
          },
        });
      });
    },

    updateItemErrorHandler(error) {
      if (error.message === 'Already Exists') {
        notify(Constants.error, 'The email field must be unique.');
      } else {
        notify(Constants.error, `Error updating email address: ${error.message}`);
      }
    },
  },
};
</script>

<style module>
.errorClass {
  font-size: 1.1rem;
  text-align: left;
}

.instructions {
  margin-top: 64px;
}

.logo {
  height: 100%;
  width: 100%;
}
</style>
